/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-373)
 * @date      2026-07-03
 * @purpose   Ponte UEM client per l'isola wishlist (pattern likeService, design §4).
 *            L'isola è STANDALONE: non chiama mai UEM.initialize() da sola — la
 *            init di uemClientService è FATAL-on-failure (alert + reload, vedi
 *            loadServerConfiguration) e va lasciata al bundle principale
 *            (main.ts/main_app.ts). Qui: UEM se pronto, console come fallback
 *            onesto — mai errori persi, mai reload innescati dall'isola.
 */

import { UEM_Client_TS_Placeholder as UEM } from '../services/uemClientService';
import type { ServerErrorResponse } from '../config/appConfig';

function isServerErrorResponse(value: unknown): value is ServerErrorResponse {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const record = value as Record<string, unknown>; // narrow post-guard
    return typeof record.error === 'string' && typeof record.message === 'string';
}

/**
 * Risposta di errore del server (UEM handle() → JSON error/message).
 * `code` null = nessun codice UEM registrato per l'operazione → solo console
 * (lezione M-EGI-265: mai codici fantasma). La lista usa WISHLIST_LIST_FAILED
 * (registrato in config/error-manager.php — remediation P2-1 M-EGI-373).
 */
export function reportServerError(
    code: string | null,
    errorData: unknown,
    context: Record<string, unknown>,
): void {
    if (code !== null && UEM.isReady() && isServerErrorResponse(errorData)) {
        UEM.handleServerErrorResponse(errorData, undefined);
        return;
    }
    console.error('[wishlist] server error', code, errorData, context);
}

/** Errore client (rete, parse). Stessa politica: UEM se pronto, altrimenti console. */
export function reportClientError(
    code: string | null,
    context: Record<string, unknown>,
    original?: Error,
): void {
    if (code !== null && UEM.isReady()) {
        UEM.handleClientError(code, context, original);
        return;
    }
    console.error('[wishlist] client error', code, context, original);
}
